import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime, formatMoney } from '../ui/format';
import { Badge, EmptyState, Loading, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  createAgent,
  createFromTemplate,
  decideApproval,
  listApprovals,
  listTemplates,
  type Approval,
  getAgent,
  getExecution,
  getUsage,
  listAgents,
  listExecutions,
  pauseAgent,
  publishVersion,
  resumeAgent,
  runAgent,
  type AgentView,
  type ExecutionDetail,
  type ExecutionView,
  type RunResult,
} from './api';

/**
 * AI agents (context.md sections 87-99).
 *
 * The screen is arranged around the two questions a tenant actually has about an agent: what is it
 * allowed to do, and what did it just do. So the tool allowlist is the centre of the editor rather
 * than a detail, and a run reads back step by step — including the tools it asked for and was
 * refused, which is the part somebody needs when an agent behaves oddly.
 */

const STATUS_TONE: Record<AgentView['status'], BadgeTone> = {
  DRAFT: 'neutral',
  ACTIVE: 'positive',
  PAUSED: 'warning',
  ARCHIVED: 'neutral',
};

const RUN_TONE: Record<string, BadgeTone> = {
  AWAITING_APPROVAL: 'warning',
  COMPLETED: 'positive',
  RUNNING: 'info',
  STOPPED: 'warning',
  REFUSED: 'warning',
  FAILED: 'danger',
};

export function AgentsPage(): React.JSX.Element {
  const client = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');

  const agents = useQuery({ queryKey: ['ai-agents'], queryFn: listAgents });
  const usage = useQuery({ queryKey: ['ai-usage'], queryFn: getUsage });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['ai-agents'] });
    void client.invalidateQueries({ queryKey: ['ai-agent', selected] });
  };

  const create = useMutation({
    mutationFn: () => createAgent({ name: name.trim() }),
    onSuccess: (agent) => {
      setName('');
      setError(null);
      setSelected(agent.id);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const templates = useQuery({
    queryKey: ['ai-templates'],
    queryFn: listTemplates,
    staleTime: 10 * 60 * 1000,
  });

  const fromTemplate = useMutation({
    mutationFn: (key: string) => createFromTemplate(key),
    onSuccess: (agent) => {
      setError(null);
      setSelected(agent.id);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const pause = useMutation({
    mutationFn: (agent: AgentView) =>
      agent.status === 'ACTIVE' ? pauseAgent(agent.id) : resumeAgent(agent.id),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<AgentView>[] = [
    {
      key: 'name',
      header: t('ai.agent'),
      render: (agent) => (
        <span className="cell-stack">
          <span className="strong">{agent.name}</span>
          <span className="muted small">{agent.role}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      render: (agent) => (
        <span className="cell-stack">
          <Badge tone={STATUS_TONE[agent.status]}>{t(`ai.state.${agent.status}`)}</Badge>
          {agent.published_version && (
            <span className="muted small">
              {t('ai.versionLabel').replace('{n}', String(agent.published_version))}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (agent) => (
        <span className="cell-inline">
          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => setSelected(agent.id)}
          >
            {t('ai.open')}
          </button>

          {agent.published_version !== null && (
            <button
              type="button"
              className="button button--quiet button--compact"
              disabled={pause.isPending}
              onClick={() => pause.mutate(agent)}
            >
              {agent.status === 'ACTIVE' ? t('ai.pause') : t('ai.resume')}
            </button>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="view">
      <PageHeader
        title={t('nav.agents')}
        description={t('ai.description')}
        actions={
          usage.data && (
            <span className="cell-inline">
              <span className="muted small">{t('ai.spent30')}</span>
              <span className="strong">{formatMoney(usage.data.cost_cents)}</span>
              <span className="muted small">
                {t('ai.tokens').replace(
                  '{n}',
                  String(usage.data.prompt_tokens + usage.data.completion_tokens),
                )}
              </span>
            </span>
          )
        }
      />

      <Panel title={t('ai.newAgent')}>
        <div className="form-grid">
          <label className="field">
            <span>{t('ai.agentName')}</span>
            <input value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />
          </label>
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="button button--primary"
          disabled={name.trim() === '' || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? t('work.creating') : t('ai.createAgent')}
        </button>
      </Panel>

      {/*
       * Agents to start from (section 90).
       *
       * Each becomes an ordinary agent the moment it is created — a starting prompt, a tool list
       * and a sensible approval list, then it is the tenant's to edit. Nothing about it stays
       * special, which is the point: behaviour that lives in code is behaviour nobody can tune
       * without a deploy.
       */}
      <Panel title={t('ai.startFrom')}>
        <p className="muted small">{t('ai.startFromHint')}</p>

        <div className="template-grid">
          {(templates.data?.templates ?? []).map((template) => (
            <div key={template.key} className="template">
              <span className="strong">{template.name}</span>
              <span className="muted small">{template.description}</span>
              <span className="muted small">
                {t('ai.templateTools').replace('{n}', String(template.tools.length))}
                {template.approvalTools.length > 0 &&
                  ` · ${t('ai.templateGuarded').replace('{n}', String(template.approvalTools.length))}`}
              </span>
              <button
                type="button"
                className="button button--quiet button--compact"
                disabled={fromTemplate.isPending}
                onClick={() => fromTemplate.mutate(template.key)}
              >
                {t('ai.templateUse')}
              </button>
            </div>
          ))}
        </div>
      </Panel>

      <Approvals />

      <Panel>
        <DataTable
          tableKey="agents"
          rows={agents.data?.agents}
          columns={columns}
          rowKey={(agent) => agent.id}
          isLoading={agents.isLoading}
          error={agents.error}
          onRetry={() => void agents.refetch()}
          empty={{ title: t('ai.noAgents'), description: t('ai.noAgentsBody') }}
        />
      </Panel>

      {selected && <AgentDetail id={selected} onChanged={refresh} />}
    </div>
  );
}

function AgentDetail({ id, onChanged }: { id: string; onChanged: () => void }): React.JSX.Element {
  const [instructions, setInstructions] = useState('');
  const [tools, setTools] = useState<string[]>([]);
  const [approvalTools, setApprovalTools] = useState<string[] | null>(null);
  const [message, setMessage] = useState('');
  const [result, setResult] = useState<RunResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openRun, setOpenRun] = useState<ExecutionDetail | null>(null);

  const agent = useQuery({
    queryKey: ['ai-agent', id],
    queryFn: () => getAgent(id),
  });

  const runs = useQuery({
    queryKey: ['ai-executions', id],
    queryFn: () => listExecutions(id),
  });

  const publish = useMutation({
    mutationFn: () =>
      publishVersion(id, {
        instructions: instructions.trim(),
        tools,
        approvalTools: (approvalTools ?? current?.approval_tools ?? []).filter((tool) =>
          (tools.length > 0 ? tools : (current?.tools ?? [])).includes(tool),
        ),
      }),
    onSuccess: () => {
      setError(null);
      void agent.refetch();
      onChanged();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const ask = useMutation({
    mutationFn: () => runAgent(id, message.trim()),
    onSuccess: (data) => {
      setResult(data);
      setMessage('');
      setError(null);
      void runs.refetch();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const inspect = useMutation({
    mutationFn: (executionId: string) => getExecution(executionId),
    onSuccess: (data) => setOpenRun(data),
  });

  if (agent.isLoading) return <Loading />;
  if (!agent.data) return <EmptyState title={t('ai.noAgents')} description="" />;

  const current = agent.data.versions.find(
    (version) => version.version === agent.data.published_version,
  );

  const runColumns: Column<ExecutionView>[] = [
    {
      key: 'input',
      header: t('ai.question'),
      render: (execution) => (
        <span className="cell-stack">
          <span>{execution.input}</span>
          {execution.output && <span className="muted small">{execution.output}</span>}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      render: (execution) => (
        <span className="cell-stack">
          <Badge tone={RUN_TONE[execution.status] ?? 'neutral'}>
            {t(`ai.run.${execution.status}`)}
          </Badge>
          <span className="muted small">
            {t('ai.stepCount').replace('{n}', String(execution.steps))}
          </span>
        </span>
      ),
    },
    {
      key: 'when',
      header: t('ai.when'),
      align: 'end',
      secondary: true,
      render: (execution) => formatDateTime(execution.started_at),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (execution) => (
        <button
          type="button"
          className="button button--quiet button--compact"
          onClick={() => inspect.mutate(execution.id)}
        >
          {t('ai.inspect')}
        </button>
      ),
    },
  ];

  return (
    <div className="home-grid">
      <Panel title={t('ai.behaviour')}>
        <p className="muted small">{t('ai.versionHint')}</p>

        <label className="field">
          <span>{t('ai.instructions')}</span>
          <textarea
            rows={6}
            value={instructions || (current?.instructions ?? '')}
            onChange={(event) => setInstructions(event.target.value)}
          />
        </label>

        <fieldset className="field">
          <legend>{t('ai.tools')}</legend>
          <p className="muted small">{t('ai.toolsHint')}</p>

          <div className="chips">
            {agent.data.available_tools.map((tool) => {
              const chosen = (tools.length > 0 ? tools : (current?.tools ?? [])).includes(tool);

              return (
                <button
                  key={tool}
                  type="button"
                  className={`chip${chosen ? ' chip--active' : ''}`}
                  onClick={() =>
                    setTools((currentTools) => {
                      const base = currentTools.length > 0 ? currentTools : (current?.tools ?? []);

                      return base.includes(tool)
                        ? base.filter((name) => name !== tool)
                        : [...base, tool];
                    })
                  }
                >
                  {tool}
                </button>
              );
            })}
          </div>
        </fieldset>

        {/*
         * Which of those wait for a person (section 92).
         *
         * Offered only among the tools chosen above: a guard on a tool the agent cannot call guards
         * nothing while looking like it does, and the server refuses it for the same reason.
         */}
        <fieldset className="field">
          <legend>{t('ai.approvalTools')}</legend>
          <p className="muted small">{t('ai.approvalToolsHint')}</p>

          <div className="chips">
            {(tools.length > 0 ? tools : (current?.tools ?? [])).map((tool) => {
              const guarded = (approvalTools ?? current?.approval_tools ?? []).includes(tool);

              return (
                <button
                  key={tool}
                  type="button"
                  className={`chip${guarded ? ' chip--active' : ''}`}
                  onClick={() =>
                    setApprovalTools((currentGuarded) => {
                      const base = currentGuarded ?? current?.approval_tools ?? [];

                      return base.includes(tool)
                        ? base.filter((name) => name !== tool)
                        : [...base, tool];
                    })
                  }
                >
                  {guarded ? '🔒 ' : ''}
                  {tool}
                </button>
              );
            })}
          </div>
        </fieldset>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="button button--primary"
          disabled={publish.isPending}
          onClick={() => publish.mutate()}
        >
          {t('ai.publish')}
        </button>

        {current && (
          <p className="muted small">
            {t('ai.publishedAs')
              .replace('{n}', String(current.version))
              .replace('{hash}', current.content_hash.slice(0, 12))}
          </p>
        )}
      </Panel>

      <Panel title={t('ai.try')}>
        <label className="field">
          <span>{t('ai.question')}</span>
          <textarea rows={3} value={message} onChange={(event) => setMessage(event.target.value)} />
        </label>

        <button
          type="button"
          className="button button--primary"
          disabled={message.trim() === '' || ask.isPending || agent.data.status !== 'ACTIVE'}
          onClick={() => ask.mutate()}
        >
          {ask.isPending ? t('ai.thinking') : t('ai.ask')}
        </button>

        {result && (
          <div className="stack">
            {result.status === 'AWAITING_APPROVAL' && (
              <p className="alert small">{t('ai.parkedHint')}</p>
            )}
            <p>{result.output}</p>
            <dl className="summary">
              <div>
                <dt>{t('ai.stepsLabel')}</dt>
                <dd>{result.steps}</dd>
              </div>
              <div>
                <dt>{t('ai.tokensLabel')}</dt>
                <dd>{result.tokens.prompt + result.tokens.completion}</dd>
              </div>
              <div>
                <dt>{t('ai.costLabel')}</dt>
                <dd>{formatMoney(result.costCents)}</dd>
              </div>
            </dl>
          </div>
        )}
      </Panel>

      <Panel title={t('ai.runs')}>
        <DataTable
          tableKey="agents-2"
          rows={runs.data?.executions}
          columns={runColumns}
          rowKey={(execution) => execution.id}
          isLoading={runs.isLoading}
          error={runs.error}
          onRetry={() => void runs.refetch()}
          empty={{ title: t('ai.noRuns'), description: t('ai.noRunsBody') }}
        />

        {openRun && (
          <ol className="home-list">
            {openRun.trace.map((step) => (
              <li key={step.position}>
                <span className="cell-inline">
                  <Badge tone={step.failure_reason ? 'danger' : 'neutral'}>{step.kind}</Badge>
                  <code>{step.name}</code>
                  {step.failure_reason && (
                    <span className="muted small">{t(`ai.refusal.${step.failure_reason}`)}</span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        )}
      </Panel>
    </div>
  );
}

/**
 * Questions agents are waiting on (section 92).
 *
 * The arguments are shown verbatim, because a person approves *what the model wants to do*, not the
 * idea of it: "add a note" is harmless, "add a note saying the discount is approved" is not, and
 * the difference is in the arguments. A refusal needs a reason — it goes back to the model, which
 * goes back to a customer, and a bare "no" leaves the model to invent one.
 */
function Approvals(): React.JSX.Element | null {
  const client = useQueryClient();
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const pending = useQuery({
    queryKey: ['ai-approvals'],
    queryFn: () => listApprovals(['PENDING']),
    // Somebody may be waiting on this screen for a run to ask; keep it fresh without a refresh.
    refetchInterval: 10_000,
  });

  const decide = useMutation({
    mutationFn: (input: { id: string; approved: boolean }) =>
      decideApproval(input.id, {
        approved: input.approved,
        reason: reasons[input.id]?.trim() || null,
      }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['ai-approvals'] });
      void client.invalidateQueries({ queryKey: ['ai-executions'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const rows = pending.data?.approvals ?? [];

  if (pending.isLoading || rows.length === 0) return null;

  return (
    <Panel title={t('ai.approvals')}>
      <p className="muted small">{t('ai.approvalsHint')}</p>

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}

      <ul className="hits">
        {rows.map((approval: Approval) => (
          <li key={approval.id} className="hit">
            <div className="hit__head">
              <span className="strong small">
                {approval.agentName} · <code>{approval.toolName}</code>
              </span>
              <span className="muted small">{formatDateTime(approval.requestedAt)}</span>
            </div>

            <pre className="approval__args">{JSON.stringify(approval.arguments, null, 2)}</pre>

            <div className="approval__actions">
              <input
                className="input"
                value={reasons[approval.id] ?? ''}
                placeholder={t('ai.rejectReason')}
                maxLength={1000}
                onChange={(event) =>
                  setReasons((current) => ({ ...current, [approval.id]: event.target.value }))
                }
              />
              <button
                type="button"
                className="button button--quiet button--compact"
                disabled={decide.isPending || !(reasons[approval.id] ?? '').trim()}
                onClick={() => decide.mutate({ id: approval.id, approved: false })}
              >
                {t('ai.reject')}
              </button>
              <button
                type="button"
                className="button button--primary button--compact"
                disabled={decide.isPending}
                onClick={() => decide.mutate({ id: approval.id, approved: true })}
              >
                {t('ai.approve')}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
